/**
 * FETCHING THE TEXT, AND KNOWING WHEN IT IS NOT COMING.
 *
 * The verses do not travel in the first download — 123,011 passages come to about 90 MB,
 * which is not a page you can open on a phone. They arrive in ranges, on demand, and this
 * tracks which of those are here, in flight, or given up on.
 *
 * The transport is injected. The web appends a <script> and a mobile app will fetch JSON;
 * neither of those is the interesting part. The interesting part is what a reader is told
 * while they wait, and what they are told when the waiting should stop — which is the same
 * on both, and was wrong on the web for a long time.
 *
 * TWO DEFECTS THIS SHAPE EXISTS TO PREVENT, both of which shipped:
 *
 *  1. "Fetching these verses…" for ever. Honest for two seconds, a lie after ten, and a
 *     reader left waiting for something that was never coming.
 *  2. `ready()` answering the wrong question. It used to mean "is there anything left to
 *     request", which is identical to "is every passage here to render" only while nothing
 *     can fail. Once a range could give up, they diverged — and the panel drew its
 *     headings over nothing at all, because the loader reported success while the text was
 *     still missing.
 */

export type ChunkState = 'here' | 'inFlight' | 'gaveUp';

export interface ChunkLoaderOptions {
  /** Which range holds a passage. */
  chunkOf: (verseId: number) => number;
  /**
   * Start fetching a range. Call `arrived` on success and `failed` on error; the loader
   * also gives up on its own after `timeoutMs`, because a request that never settles is
   * indistinguishable from one that never arrives.
   */
  request: (chunk: number, arrived: () => void, failed: () => void) => void;
  /** Called whenever the picture changes, so the interface can redraw. */
  onChange?: () => void;
  /** How long to wait before saying so. */
  timeoutMs?: number;
  setTimer?: (fn: () => void, ms: number) => unknown;
}

export class ChunkLoader {
  private here = new Set<number>();
  private inFlight = new Set<number>();
  private gaveUp = new Set<number>();
  private opts: Required<Pick<ChunkLoaderOptions, 'chunkOf' | 'request'>> & ChunkLoaderOptions;

  constructor(opts: ChunkLoaderOptions) {
    this.opts = { timeoutMs: 12000, ...opts };
  }

  /** A range has landed. */
  markArrived(chunk: number) {
    this.inFlight.delete(chunk);
    this.gaveUp.delete(chunk);
    this.here.add(chunk);
    this.opts.onChange?.();
  }

  state(chunk: number): ChunkState | 'unknown' {
    if (this.here.has(chunk)) return 'here';
    if (this.inFlight.has(chunk)) return 'inFlight';
    if (this.gaveUp.has(chunk)) return 'gaveUp';
    return 'unknown';
  }

  /**
   * Ask for whatever these passages need, and say whether they can be rendered NOW.
   *
   * The return value answers "is every passage here", not "did I request anything" — see
   * the header. A failed range is deliberately not re-requested: the failure triggers the
   * redraw, and a redraw that re-requests it spins forever.
   */
  ensure(verseIds: number[]): boolean {
    const need = new Set<number>();
    for (const id of verseIds ?? []) need.add(this.opts.chunkOf(id));

    const missing = [...need].filter(c => !this.here.has(c));
    if (!missing.length) return true;

    for (const c of missing) {
      /*
       * `here` is re-checked, not just `inFlight`. A range can land WHILE this loop is
       * running — a synchronous transport does exactly that, and so does a fast cache —
       * and its arrival triggers the redraw, which calls back in and may pull the rest.
       * By the time the loop reaches those, they are already here, and `missing` was
       * computed before any of it. Without this the page asked for 28 ranges to get 17.
       */
      if (this.here.has(c) || this.inFlight.has(c) || this.gaveUp.has(c)) continue;
      this.inFlight.add(c);

      const giveUp = () => {
        if (!this.inFlight.has(c)) return;      // it landed after all
        this.inFlight.delete(c);
        this.gaveUp.add(c);
        this.opts.onChange?.();
      };

      this.opts.request(c, () => this.markArrived(c), giveUp);

      /* Armed AFTER the request is out. Registering it first meant that under a
         synchronous transport — which the headless checks use — the timer fired before
         the request had a chance to run, and every range was marked failed instantly. */
      const timer = this.opts.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms));
      timer(giveUp, this.opts.timeoutMs!);
    }
    return false;
  }

  /**
   * Of these passages, which are in ranges that gave up.
   *
   * The interface needs this to tell a reader the difference between "not yet" and "not
   * coming", and to offer the public site for the ones that are not coming — a link that
   * needs nothing from this page, which is exactly what makes it the right fallback.
   */
  abandoned(verseIds: number[]): number[] {
    return (verseIds ?? []).filter(id => this.gaveUp.has(this.opts.chunkOf(id)));
  }

  /** Whether anything is still expected, as against given up on. */
  waiting(verseIds: number[]): boolean {
    return (verseIds ?? []).some(id => {
      const c = this.opts.chunkOf(id);
      return !this.here.has(c) && !this.gaveUp.has(c);
    });
  }
}

/**
 * Which range holds a passage, by binary search over the range starts.
 *
 * Linear search was fine at three ranges and is not at eighty-four, and this runs for
 * every passage of every panel.
 */
export function chunkOfId(starts: number[], id: number): number {
  let lo = 0, hi = starts.length - 1, ans = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (starts[mid] <= id) { ans = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return ans;
}
